import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "@/lib/utils";
import {
  CommandInput,
  CommandEmpty,
  CommandList,
  CommandGroup,
  CommandItem,
  Command,
} from "../ui/command";
import { ChevronsUpDownIcon, Check, LoaderCircle } from "lucide-react";
import { Button } from "../ui/button";
import { getRequestHUBs } from "@/services/hub-list.service";
import { useEffect, useState } from "react";
import { HUB_ } from "@/interfaces/HUB";
import { ReturnRequest } from "@/interfaces/Return";
import { PRIMARY_COLOR } from "@/constants/constants";

interface RequestDropOffSelectProps {
  selectedRequest: ReturnRequest;
  selectedDropOff: HUB_;
  setSelectedDropOff: (dropOff: HUB_) => void;
}

const RequestDropOffSelect: React.FC<RequestDropOffSelectProps> = ({
  selectedRequest,
  selectedDropOff,
  setSelectedDropOff,
}) => {
  const [dropOffOpen, setDropOffOpen] = useState(false);
  const [dropOffs, setDropOffs] = useState<HUB_[]>([]);
  const [loading, setHubLoading] = useState(false);

  const fetchHUBs = async () => {
    setHubLoading(true);
    try {
      const response = await getRequestHUBs(
        selectedRequest?.num.toString() || ""
      );
      if (response.status === 200 && response.data.success === true) {
        setDropOffs(response.data.data);

        const defaultDropOff = response.data.data.find(
          (hub) => hub.isDefault === true
        );
        if (defaultDropOff) {
          setSelectedDropOff(defaultDropOff);
        }

        setHubLoading(false);
      } else {
        setHubLoading(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setHubLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRequest?.num) {
      fetchHUBs();
    } else {
      setDropOffs([]);
      setSelectedDropOff({} as HUB_);
    }
  }, [selectedRequest]);

  return (
    <div className="grid gap-1 ">
      <label className="text-sm font-medium" htmlFor="supplier">
        Point de drop-off
      </label>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col w-full gap-1">
          <Popover open={dropOffOpen} onOpenChange={setDropOffOpen}>
            <PopoverTrigger asChild>
              <Button
                className="w-full justify-between items-center"
                role="combobox"
                variant="outline"
              >
                <div className="flex items-center gap-1">
                  {selectedDropOff?.id
                    ? selectedDropOff.name
                    : "Choisir un point de drop-off"}
                  {selectedDropOff.isDefault && (
                    <label
                      className="text-xs  px-1"
                      style={{ color: PRIMARY_COLOR }}
                    >
                      Proposé par le vendeur
                    </label>
                  )}
                </div>
                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height]">
              <Command>
                <CommandInput
                  className="h-9"
                  placeholder="Chercher un point de drop-off"
                />
                <CommandEmpty>Aucune demande trouvée</CommandEmpty>
                <CommandList>
                  <CommandGroup>
                    {dropOffs &&
                      dropOffs.map((dropOff) => (
                        <CommandItem
                          key={dropOff.id}
                          value={dropOff.name}
                          onSelect={() => {
                            setSelectedDropOff(dropOff);
                            setDropOffOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              dropOff.id === selectedDropOff?.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          {dropOff.name}
                          {dropOff.isDefault && (
                            <label
                              className="text-xs px-1"
                              style={{ color: PRIMARY_COLOR }}
                            >
                              Proposé par le vendeur
                            </label>
                          )}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        {loading && (
          <LoaderCircle className="animate-spin" color="grey" size={20} />
        )}
      </div>
    </div>
  );
};

export default RequestDropOffSelect;
